import { hasItems } from "utils/discovery";
import { FILTER_TYPES } from "../../../constants/discovery";

export const addAfter = (state, index, newItems) => {
  newItems.forEach((np) => {
    np.collapsed = false;
  });

  state.themes = [...state.themes.slice(0, index)]
    .concat(newItems)
    .concat([...state.themes.slice(index)]);
};

export const setThemesItems = (state, action) => {
  const items = action.payload;

  items.forEach((item) => {
    const insertAt = state.themes.findIndex(
      (x) => item.GroupName === x.GroupName && !x.NounPhrase
    );

    state.themes[insertAt].NounPhrases = item.NounPhrases;

    // add GroupName to NounPhrase
    const updatedNounPhrases = state.themes[insertAt].NounPhrases.map((np) => ({
      ...np,
      GroupName: state.themes[insertAt].GroupName,
    }));

    addAfter(state, insertAt + 1, updatedNounPhrases);
  });
};

// Set given list of docsToSearch to be ignored if in list of docsToBeIgnored
const ignoreDocumentsInList = (docsToBeIgnored, docsToSearch) =>
  docsToSearch.map((doc) => ({
    ...doc,
    ignored: docsToBeIgnored.find((headDoc) => headDoc.Id === doc.Id).ignored,
  }));

// Get an array with the num of occurrences of all the ignored docs
// for calculating the noun phrase occurrences with the current ignored docs state
const docOccurrences = (docsToBeIgnored, docsToSearch) =>
  docsToSearch.map((document) => {
    if (
      docsToBeIgnored.find(
        (headDoc) => headDoc.Id === document.Id && headDoc.ignored
      )
    ) {
      return document.Occurrences;
    }
    return 0;
  });

const addExpandedNps = (state, i, action) => {
  // Slice the array to include the noun phrases on the correct position
  state.themes = state.themes
    .slice(0, i)
    .concat({
      ...state.themes[i],
      NounPhrases: action.payload.NounPhrases.map((np) => np.NounPhrase),
    })
    .concat(
      action.payload.NounPhrases.map((nounPhrase) => {
        const reducer = (total, current) => total - current;
        const occurrencesArr = docOccurrences(
          state.themes[0].Documents,
          nounPhrase.Documents
        );

        return {
          ...nounPhrase,
          // Get occurrences taking into account ignored docs
          Occurrences: occurrencesArr.reduce(reducer, nounPhrase.Occurrences),
          // See if noun phrase is checked on another theme
          checked:
            nounPhrase.checked ||
            state.themes.find(
              (item) => item.NounPhrase === nounPhrase.NounPhrase
            )?.checked,
          Documents: ignoreDocumentsInList(
            state.themes[0].Documents,
            nounPhrase.Documents
          ),
        };
      })
    )
    .concat(state.themes.slice(i + 1));
};

export const expandCollapseTheme = (state, groupName, collapsed) => {
  state.themes = state.themes.map((item) => {
    if (item.GroupName === groupName && item.NounPhrase) {
      return { ...item, collapsed };
    }
    return item;
  });
};

export const expandTheme = (state, action) => {
  const nps = state.themes.filter(
    (t) => t.GroupName === action.payload.GroupName && t.NounPhrase
  );
  const groupNameIndex = state.themes.findIndex(
    (x) => action.payload.GroupName === x.GroupName && !x.NounPhrase
  );

  if (!nps.length) {
    // If there are no nps in state, just insert new nps
    addExpandedNps(state, groupNameIndex, action);
  } else if (state.config.filterView) {
    /* If there are checked nps in state and predefined filter applied */
    const checkedNPS = nps.filter((np) => np.checked);

    // Create new NPs
    const newNPs = action.payload.NounPhrases;
    checkedNPS.forEach((checkedNP) => {
      const newNPIndex = newNPs.findIndex(
        (newNP) => newNP.NounPhrase === checkedNP.NounPhrase
      );
      if (newNPs[newNPIndex]) {
        // If checked item is already present in new NPs, just check it
        newNPs[newNPIndex].checked = true;
        newNPs[newNPIndex].selected = true;
      } else {
        // Else add it to new NPs
        newNPs.push({
          ...checkedNP,
        });
      }
    });

    // Remove old NPs
    state.themes = state.themes.filter(
      (theme) =>
        !(theme.GroupName === action.payload.GroupName && theme.NounPhrase)
    );

    // Insert new NPs to theme list
    action.payload.NounPhrases = newNPs;
    addExpandedNps(state, groupNameIndex, action);
  } else {
    // If nps are already in state, just open category
    expandCollapseTheme(state, action.payload.GroupName, false);
  }
};

export const collapseAllThemes = (state) => {
  state.themes = state.themes.map((item) => {
    if (item.NounPhrase) {
      return { ...item, collapsed: true };
    }
    return item;
  });
};

/**
 * Checks / unchecks a Noun Phrase on all themes where it exists
 * Only want to update themes 'checked' param if it is not a search. This is because searches do not
 * return all items for a theme, so we cannot correctly check a theme at this stage
 */
const checkUncheckNounPhraseInThemes = (state, theme, checked, isSearch) => {
  // update checked noun phrases
  state.themes = state.themes.map((t) =>
    t.NounPhrase === theme.NounPhrase
      ? {
          ...t,
          checked,
        }
      : t
  );
  state.config.saveSnapshotDisabled = false;

  // update checked themes
  if (!isSearch) {
    const itemsInTheme = state.themes.find(
      (t) => t.GroupName === theme.GroupName && t.NounPhrases
    ).NounPhrases.length;
    const checkedItemsInTheme = state.themes.filter(
      (t) => t.GroupName === theme.GroupName && t.NounPhrase && t.checked
    ).length;

    state.themes = state.themes.map((t) =>
      t.GroupName === theme.GroupName && t.NounPhrases
        ? {
            ...t,
            checked: itemsInTheme === checkedItemsInTheme,
          }
        : t
    );
  }
};

/**
 * Check / uncheck a theme, and it's noun phrases
 * as well as the noun phrases repeated on other themes
 * If theme is All Themes it will uncheck all themes
 */
const checkUncheckTheme = (state, theme, checked) => {
  const { isSearch, filterView, showAll } = state.config;

  state.themes = state.themes.map((t) => {
    if (
      t.GroupName === theme.GroupName ||
      Boolean(
        theme.NounPhrases &&
          t.NounPhrase &&
          theme.NounPhrases.find((np) => (np.NounPhrase || np) === t.NounPhrase)
      )
    ) {
      if (isSearch) {
        if (t.FilteredBySearch) {
          return { ...t, checked };
        }

        return t;
      }

      if (filterView) {
        if (t.FilteredByFilter) {
          return { ...t, checked };
        }

        return t;
      }

      if (!showAll) {
        if (t.selected) {
          return { ...t, checked };
        }

        return t;
      }

      return { ...t, checked };
    }

    return t;
  });

  state.config.saveSnapshotDisabled = false;
};

/* Checks / unchecks a theme and expands the collapsed noun phrases */
const expandCollapseCheckUncheckThemeInThemes = (
  state,
  theme,
  checked,
  collapsed
) => {
  expandCollapseTheme(state, theme.GroupName, collapsed);
  checkUncheckTheme(state, theme, checked);
};

const checkThemeAndAddNps = (state, theme, i, nounPhrases) =>
  // Slice the array to include the noun phrases on the correct position
  {
    state.themes = state.themes
      .slice(0, i)
      .map((item) => {
        // Check new noun phrases if are already checked in another theme
        if (nounPhrases.indexOf(item.NounPhrase) > -1) {
          return { ...item, checked: true };
        }
        return item;
      })
      .concat({
        ...state.themes[i],
        NounPhrases: nounPhrases,
        checked: true,
      })
      .concat(
        theme.NounPhrases.map((nounPhrase) => {
          const reducer = (total, current) => total - current;
          const occurrencesArr = docOccurrences(
            state.themes[0].Documents,
            nounPhrase.Documents
          );

          return {
            ...nounPhrase,
            // Get occurrences taking into account ignored docs
            Occurrences: occurrencesArr.reduce(reducer, nounPhrase.Occurrences),
            Documents: ignoreDocumentsInList(
              state.themes[0].Documents,
              nounPhrase.Documents
            ),
          };
        })
      )
      .concat(
        state.themes.slice(i + 1).map((item) => {
          if (nounPhrases.indexOf(item.NounPhrase) > -1) {
            return { ...item, checked: true };
          }
          return item;
        })
      );

    state.config.saveSnapshotDisabled = false;
  };

export const check = (state, action) => {
  const theme = action.payload;
  const { isSearch } = state.config;

  // If it is a noun phrase
  if (theme.NounPhrase) {
    checkUncheckNounPhraseInThemes(state, theme, true, isSearch);
    return;
  }

  // If it is a group
  const nps = state.themes.filter(
    (t) => t.GroupName === theme.GroupName && t.NounPhrase
  );
  const groupNameIndex = [...state.themes].findIndex(
    (x) => theme.GroupName === x.GroupName && !x.NounPhrase
  );
  const nounPhrases = theme.NounPhrases?.map((np) => np.NounPhrase);

  if (!nps.length) {
    // If there are no nps in state, just insert new nps
    checkThemeAndAddNps(state, theme, groupNameIndex, nounPhrases);
  } else if (state.config.filterView) {
    /* If there are checked nps in state and predefined filter applied */
    const checkedNPS = nps.filter((np) => np.checked);

    // Create new NPs
    const newNPs = theme.NounPhrases;
    checkedNPS.forEach((checkedNP) => {
      const newNPIndex = newNPs.findIndex(
        (newNP) => newNP.NounPhrase === checkedNP.NounPhrase
      );
      if (!newNPs[newNPIndex]) {
        newNPs.push({
          ...checkedNP,
        });
      }
    });

    // Remove old NPs
    state.themes = state.themes.filter(
      (t) => !(t.GroupName === theme.GroupName && t.NounPhrase)
    );

    // Insert new NPs to theme list
    theme.NounPhrases = newNPs;
    checkThemeAndAddNps(state, theme, groupNameIndex, nounPhrases);
  } else {
    // If nps are already in state, just open category
    expandCollapseCheckUncheckThemeInThemes(state, theme, true, false);
  }
};

export const uncheckAllThemes = (state) => {
  state.themes = state.themes.map((theme) => ({ ...theme, checked: false }));
};

export const uncheckAndCollapse = (state, action) => {
  const theme = action.payload;
  const { isSearch } = state.config;

  if (!theme.NounPhrase) {
    expandCollapseCheckUncheckThemeInThemes(state, theme, false, true);
  } else {
    checkUncheckNounPhraseInThemes(state, theme, false, isSearch);
  }
};

export const addIgnoredDocument = (state, action) => {
  state.themes = state.themes.map((theme) => {
    const ignored = theme.Documents?.find((doc) => doc.Id === action.payload);
    const documents = theme.Documents.map((doc) => {
      if (doc.Id === ignored?.Id) {
        return { ...doc, ignored: true };
      }
      return doc;
    });
    if (ignored !== undefined) {
      let updatedTheme = {
        ...theme,
        Occurrences: theme.Occurrences - ignored?.Occurrences,
        Documents: documents,
      };
      if (ignored.Search) {
        updatedTheme = {
          ...updatedTheme,
          SearchOccurrences:
            theme.SearchOccurrences - ignored.Search.Occurrences,
        };
      }
      return updatedTheme;
    }
    return theme;
  });
};

export const removeIgnoredDocument = (state, action) => {
  state.themes = state.themes.map((theme) => {
    const unIgnored = theme.Documents?.find((doc) => doc.Id === action.payload);
    const documents = theme.Documents.map((doc) => {
      if (doc.Id === unIgnored?.Id) {
        return { ...doc, ignored: false };
      }
      return doc;
    });
    if (unIgnored !== undefined) {
      let updatedTheme = {
        ...theme,
        Occurrences: theme.Occurrences + unIgnored?.Occurrences,
        Documents: documents,
      };
      if (unIgnored.Search) {
        updatedTheme = {
          ...updatedTheme,
          SearchOccurrences:
            theme.SearchOccurrences + unIgnored.Search.Occurrences,
        };
      }
      return updatedTheme;
    }
    return theme;
  });
};

export const removeNounPhrases = (state) => {
  state.themes = state.themes.filter((element) => !element.NounPhrase);
};

const removeFilteredBySearch = (state) => {
  state.themes = state.themes.map((theme) => ({
    ...theme,
    FilteredBySearch: false,
  }));
};

const removeCheckedThemes = (state) => {
  state.themes = state.themes.map((theme) => ({
    ...theme,
    checked: false,
  }));
};

const resetView = (state) => {
  removeNounPhrases(state);
  removeFilteredBySearch(state);
  removeCheckedThemes(state);
};

export const resetThemes = (state) => {
  state.themes[0].Documents.filter((doc) => doc.ignored).forEach((doc) => {
    removeIgnoredDocument(state, { payload: doc.Id });
  });

  resetView(state);
};

const setAllThemesDoc = (group, state) => {
  const allThemesDocs = state.themes[0].Documents;

  for (let i = 0; i < allThemesDocs.length; i += 1) {
    if (!allThemesDocs[i].Search) {
      allThemesDocs[i] = { ...allThemesDocs[i], Search: { Occurrences: 0 } };
    }
    allThemesDocs[i].Search.Occurrences =
      group.Documents.find((document) => document.Id === allThemesDocs[i].Id)
        ?.Occurrences + allThemesDocs[i].Search.Occurrences ||
      allThemesDocs[i].Search.Occurrences;
  }
};

const setDocSearchOcc = (group, state, themeIndex) =>
  state.themes[themeIndex].Documents.map((d) => ({
    ...d,
    Search: {
      Occurrences:
        group.Documents.find((fd) => fd.Id === d.Id)?.Occurrences || 0,
    },
  }));

const addSearchOccurrencesThemes = (state, nps) => {
  nps.forEach((np) => {
    const npIndex = state.themes.findIndex(
      (t) => t.GroupName === np.GroupName && np.NounPhrase === t.NounPhrase
    );

    if (npIndex !== -1) {
      state.themes[npIndex] = {
        ...state.themes[npIndex],
        Occurrences: np.Occurrences,
        FilteredBySearch: true,
        collapsed: false,
      };
    }
  });
};

const ignoreDocsForSearch = (state, action) => {
  state.themes = state.themes.map((theme) => {
    const ignored = theme.Documents?.find((doc) => doc.Id === action.payload);
    const documents = theme.Documents.map((doc) => {
      if (doc.Id === ignored?.Id) {
        return { ...doc, ignored: true };
      }
      return doc;
    });
    if (ignored !== undefined && theme.FilteredBySearch) {
      let updatedTheme = {
        ...theme,
        Documents: documents,
      };
      if (!updatedTheme.NounPhrases) {
        updatedTheme = {
          ...updatedTheme,
          Occurrences: updatedTheme.Occurrences - ignored?.Occurrences,
        };
      }
      if (ignored.Search) {
        updatedTheme = {
          ...updatedTheme,
          SearchOccurrences:
            theme.SearchOccurrences - ignored.Search.Occurrences,
        };
      }
      return updatedTheme;
    }
    return theme;
  });
};

export const filterThemes = (state, action) => {
  const checkedNounPhrases = new Set();
  state.themes.forEach((theme) => {
    if (theme.NounPhrase && theme.checked) {
      checkedNounPhrases.add(theme.NounPhrase);
    }
  });

  action.payload.forEach((group) => {
    const nps = group.NounPhrases.map((np) => ({
      ...np,
      FilteredBySearch: true,
      checked: checkedNounPhrases.has(np.NounPhrase),
    }));

    // Set All Themes documents Search Occurrences
    setAllThemesDoc(group, state);

    // Add Search Occurrences to Theme
    const themeIndex = state.themes.findIndex(
      (t) => t.GroupName === group.GroupName && !t.NounPhrase
    );
    const theme = {
      ...state.themes[themeIndex],
      SearchOccurrences: group.Occurrences,
      FilteredBySearch: true,
      NounPhrases: group.NounPhrases,
      Documents: setDocSearchOcc(group, state, themeIndex),
    };

    // Add updated theme to theme list
    state.themes = state.themes
      .slice(0, themeIndex)
      .concat([theme])
      .concat(state.themes.slice(themeIndex + 1));

    // Update Noun Phrases
    if (hasItems(themeIndex, group.GroupName, state)) {
      addSearchOccurrencesThemes(state, group.NounPhrases);
    } else {
      addAfter(state, themeIndex + 1, nps);
    }
  });

  // Need to ignore docs for search results
  state.themes[0].Documents.filter((doc) => doc.ignored).forEach((doc) => {
    ignoreDocsForSearch(state, { payload: doc.Id });
  });

  state.config.isSearch = true;
};

export const addSearchNounPhrasesToState = (state, action) => {
  const checked = state.themes.filter((item) => item.checked);

  // Remove noun phrases
  state.themes = state.themes.filter((item) => !item.NounPhrase);

  action.payload.forEach((group) => {
    const nps = group.NounPhrases.map((np) => ({
      ...np,
      // Find if noun phrase is checked before cancel search
      checked:
        checked.find(
          (nounPhrase) =>
            nounPhrase.GroupName === group.GroupName &&
            nounPhrase.NounPhrase === np.NounPhrase
        )?.checked || false,
      GroupName: group.GroupName,
      collapsed: false,
    }));

    const themeIndex = state.themes.findIndex(
      (t) => t.GroupName === group.GroupName && !t.NounPhrase
    );

    addAfter(state, themeIndex + 1, nps);
  });
};

const removeAllThemesDocumentSearchResults = (state) => {
  const allThemesDocs = state.themes[0]?.Documents;

  for (let i = 0; i < allThemesDocs?.length; i += 1) {
    if (allThemesDocs[i].Search) {
      allThemesDocs[i] = { ...allThemesDocs[i], Search: undefined };
    }
  }
};

const removeSearchOccurrences = (state) => {
  state.themes = state.themes.map((theme) => ({
    ...theme,
    Documents: theme.Documents.map((document) => ({
      ...document,
      Search: null,
    })),
  }));
};

export const clearSearchState = (state) => {
  removeFilteredBySearch(state);
  removeSearchOccurrences(state);
  removeAllThemesDocumentSearchResults(state);
};

export const getSelectedDocumentIds = (discoveryState) => {
  const {
    themes,
    config: { documentId, filterDocID, filterType },
  } = discoveryState;
  let documentIds;

  if (documentId) {
    documentIds = [documentId];
  } else if (filterType === FILTER_TYPES.GAPS_RELATIVE_TO_A_REFERENCE_DOC) {
    documentIds = [filterDocID];
  } else {
    documentIds = themes[0]?.Documents.reduce((docIds, doc) => {
      if (!doc.ignored) {
        docIds.push(doc.Id);
      }

      return docIds;
    }, []);
  }

  return documentIds || [];
};

// Do we still need this function?
export const getAllDocumentIds = (discoveryState) => {
  const {
    themes,
    config: { documentId, filterDocID, filterType },
  } = discoveryState;
  let documentIds;

  if (documentId) {
    documentIds = [documentId];
  } else if (
    filterDocID &&
    filterType === FILTER_TYPES.GAPS_RELATIVE_TO_A_REFERENCE_DOC
  ) {
    documentIds = [filterDocID];
  } else {
    documentIds = themes[0]?.Documents.map((doc) => doc.Id);
  }

  return documentIds;
};
